import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, textStyles, spacing } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface CalendarCardProps {
  calendar: {
    _id?: string;
    name: string;
    description?: string;
    color?: string;
    timezone?: string;
    isDefault?: boolean;
    isActive?: boolean;
    visibility?: 'private' | 'public' | 'shared';
    metadata?: {
      eventCount?: number;
    };
  };
  onPress?: (calendarId: string) => void;
  onEdit?: (calendarId: string) => void;
  onDelete?: (calendarId: string) => void;
  showActions?: boolean;
  containerStyle?: any;
}

export const CalendarCard: React.FC<CalendarCardProps> = ({
  calendar,
  onPress,
  onEdit,
  onDelete,
  showActions = true,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const handlePress = () => {
    if (onPress && calendar._id) {
      onPress(calendar._id);
    }
  };

  const handleEdit = (e: any) => {
    e?.stopPropagation();
    if (onEdit && calendar._id) {
      onEdit(calendar._id);
    }
  };

  const handleDelete = (e: any) => {
    e?.stopPropagation();
    if (onDelete && calendar._id) {
      onDelete(calendar._id);
    }
  };

  const calendarColor = calendar.color || colors.primary;
  const eventCount = calendar.metadata?.eventCount || 0;

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={[styles.content, { flexDirection: rtl.rowDirection }]}>
        {/* Color Indicator */}
        <View style={[styles.colorIndicator, { backgroundColor: calendarColor }, rtl.marginEnd(spacing.md)]} />

        {/* Calendar Info */}
        <View style={styles.infoContainer}>
          <View style={[styles.headerRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.name, rtl.marginEnd(spacing.xs)]} numberOfLines={1}>
              {calendar.name}
            </Text>
            {calendar.isDefault && (
              <View style={[styles.defaultBadge, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="star" size={12} color={colors.white} />
                <Text style={styles.defaultText}>{t('calendar.calendarCard.default')}</Text>
              </View>
            )}
          </View>

          {calendar.description && (
            <Text style={styles.description} numberOfLines={2}>
              {calendar.description}
            </Text>
          )}

          <View style={[styles.metaRow, { flexDirection: rtl.rowDirection }]}>
            <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="calendar-outline" size={14} color={colors.textLight} />
              <Text style={styles.metaText}>{t('calendar.calendarCard.eventCount', { count: eventCount })}</Text>
            </View>

            {calendar.timezone && (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="time-outline" size={14} color={colors.textLight} />
                <Text style={styles.metaText} numberOfLines={1}>
                  {calendar.timezone}
                </Text>
              </View>
            )}

            {calendar.visibility && (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons
                  name={
                    calendar.visibility === 'public'
                      ? 'globe-outline'
                      : calendar.visibility === 'shared'
                      ? 'people-outline'
                      : 'lock-closed-outline'
                  }
                  size={14}
                  color={colors.textLight}
                />
                <Text style={styles.metaText} numberOfLines={1}>
                  {calendar.visibility === 'public'
                    ? t('calendar.calendarCard.visibilityPublic')
                    : calendar.visibility === 'shared'
                    ? t('calendar.calendarCard.visibilityShared')
                    : t('calendar.calendarCard.visibilityPrivate')}
                </Text>
              </View>
            )}
          </View>

          {!calendar.isActive && (
            <View style={styles.inactiveBadge}>
              <Text style={styles.inactiveText}>{t('calendar.calendarCard.inactive')}</Text>
            </View>
          )}
        </View>

        {/* Actions */}
        {showActions && (onEdit || onDelete) && (
          <View style={[styles.actionsContainer, { flexDirection: rtl.rowDirection }, rtl.marginStart(spacing.sm)]}>
            {onEdit && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleEdit}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="create-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            )}
            {onDelete && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleDelete}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="trash-outline" size={20} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    marginBottom: spacing.md,
    shadowColor: colors.literal.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  content: {
    flexDirection: 'row',
    padding: spacing.md,
  },
  colorIndicator: {
    width: 4,
    borderRadius: 2,
  },
  infoContainer: {
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  name: {
    ...textStyles.h3,
    color: colors.text.primary,
    flex: 1,
  },
  defaultBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 8,
    gap: 4,
  },
  defaultText: {
    ...textStyles.caption,
    color: colors.white,
    fontSize: 10,
    fontWeight: '600',
  },
  description: {
    ...textStyles.body,
    color: colors.textLight,
    marginBottom: spacing.xs,
    fontSize: 13,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 12,
  },
  inactiveBadge: {
    alignSelf: 'flex-start',
    backgroundColor: withAlpha(colors.error, 0.13),
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 8,
    marginTop: spacing.xs,
  },
  inactiveText: {
    ...textStyles.caption,
    color: colors.error,
    fontSize: 10,
    fontWeight: '600',
  },
  actionsContainer: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionButton: {
    padding: spacing.xs,
  },
});

